import { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { usePermissions } from '../../contexts/PermissionContext';
import { useToast } from '../../contexts/ToastContext';
import { CommunicationNavigationContext, ConversationFeedContext } from '../../contexts/CommunicationNavigationContext';
import { communications, COMMUNICATIONS_CHANGED, communicationError, type Conversation } from '../../services/communications.service';
const ProDetailSlideOver = lazy(() => import('../pro/ProDetailSlideOver'));

export default function CommunicationsProvider({ children }: { children: ReactNode }) {
  const { permissions } = usePermissions();
  const identity = permissions?.user_id;
  const [feed, setFeed] = useState({ identity, rows: [] as Conversation[], loading: true, error: '' });
  const [target, setTarget] = useState<{ pro: string; sequence: number } | null>(null);
  const sequence = useRef(0);
  const { showToast, dismissToast } = useToast();
  const openQueue = useCallback((pro: string) => setTarget({ pro, sequence: ++sequence.current }), []);
  useEffect(() => {
    if (!identity) return;
    let active = true;
    let busy = false;
    const shown = new Map<string, { messageId: number; toastId: number }>();
    const load = async () => {
      if (busy) return;
      busy = true;
      try {
        const rows = await communications.list();
        if (!active) return;
        setFeed({ identity, rows, loading: false, error: '' });
        for (const [id, toast] of shown) {
          if (!rows.some(r => r.conversation_id === id && r.unread && r.status === 'OPEN')) dismissToast(toast.toastId);
        }
        for (const row of rows.filter(r => r.unread && r.status === 'OPEN').slice(0, 3)) {
          const old = shown.get(row.conversation_id);
          if (old?.messageId === row.last_message_id) continue;
          if (old) dismissToast(old.toastId);
          const toastId = showToast({ message: `New communication request · PRO ${row.pro}: ${row.preview}`, type: 'info', duration: 0,
            onClick: () => openQueue(row.pro) });
          shown.set(row.conversation_id, { messageId: row.last_message_id, toastId });
        }
      } catch (e) {
        if (active) setFeed(prev => ({ identity, rows: prev.identity === identity ? prev.rows : [], loading: false, error: communicationError(e) }));
      } finally { busy = false; }
    };
    void load();
    const timer = window.setInterval(() => void load(), 15000);
    window.addEventListener(COMMUNICATIONS_CHANGED, load);
    window.addEventListener('focus', load);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener(COMMUNICATIONS_CHANGED, load);
      window.removeEventListener('focus', load);
      for (const toast of shown.values()) dismissToast(toast.toastId);
    };
  }, [identity, showToast, dismissToast, openQueue]);
  const visibleFeed = feed.identity === identity ? feed : { rows: [], loading: true, error: '' };
  return <ConversationFeedContext.Provider value={visibleFeed}>
    <CommunicationNavigationContext.Provider value={{ pro: target?.pro ?? null, openQueue }}>
      {children}
      {target && <Suspense fallback={<p role="status">Opening PRO…</p>}><ProDetailSlideOver key={`${identity}:${target.sequence}`} pronumber={target.pro} isOpen initialTab="order-info" onClose={() => setTarget(null)} /></Suspense>}
    </CommunicationNavigationContext.Provider>
  </ConversationFeedContext.Provider>;
}
